import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PaymentIntent } from '@stripe/stripe-js';
import { Copy, Check, MoreHorizontal, CreditCard } from 'lucide-react';
import { PaymentMethodIcon } from './CardBrandIcon';
import { ENDPOINTS } from '../config';
import { supabase } from '../lib/supabase';
import { analytics, getSessionId } from '../utils/analytics';
import { useAuth } from '../contexts/AuthContext';
import { useLabels } from '../config/useLabels';
import { isLinkBankBrand, LINK_BANK_CARD_NAME } from '../utils/stripeFee';

type PaymentMethod = 'card' | 'zelle' | 'bank_account';

interface PaymentFormProps {
  amount: number;
  onSuccess: (id: string, intent: PaymentIntent) => void;
  onError: (error: string) => void;
  memoCode?: string;
  defaultPaymentMethod?: PaymentMethod;
  invoiceId?: string | number;
  /** Brand of the saved card that Pay will charge, or null for a newly entered card. */
  onChargedCardBrandChange?: (brand: string | null) => void;
}

export function PaymentForm({ amount, onSuccess, onError, memoCode, defaultPaymentMethod = 'card', invoiceId, onChargedCardBrandChange }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const t = useLabels();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [processingTimeout, setProcessingTimeout] = useState<NodeJS.Timeout | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>(defaultPaymentMethod);
  const [savedCard, setSavedCard] = useState<{ stripe_payment_method_id: string; brand: string | null; last4: string | null } | null>(null);
  const [useSavedCard, setUseSavedCard] = useState<boolean>(true);
  const [cardMenuOpen, setCardMenuOpen] = useState<boolean>(false);
  const [copiedField, setCopiedField] = useState<'email' | 'memo' | null>(null);

  const copyToClipboard = async (text: string, field: 'email' | 'memo') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // Clipboard API unavailable — user can still manually select the text
    }
  };

  // Helper function to get active bank account setup intent
  const getBankAccountSetupIntentId = async (): Promise<string | null> => {
    try {
      // Get the current user's parent record first
      const { data: parent, error: parentError } = await supabase
        .from('parents')
        .select('id')
        .eq('email', user?.email)
        .single();

      if (parentError || !parent) {
        console.error('Error fetching parent record:', parentError);
        return null;
      }

      const { data, error } = await supabase
        .from('bank_account_setup_intents')
        .select('setup_intent_id')
        .eq('parent_id', parent.id)
        .eq('is_active', true)
        .eq('status', 'succeeded')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching bank account setup intent:', error);
        return null;
      }

      return data?.setup_intent_id || null;
    } catch (error) {
      console.error('Error fetching bank account setup intent:', error);
      return null;
    }
  };

  // Update selected payment method when defaultPaymentMethod changes
  useEffect(() => {
    setSelectedPaymentMethod(defaultPaymentMethod);
  }, [defaultPaymentMethod]);

  // Load active saved card for the logged-in parent, if any
  useEffect(() => {
    (async () => {
      try {
        if (!user?.email) return;
        const { data: parent } = await supabase
          .from('parents')
          .select('id')
          .eq('email', user.email)
          .maybeSingle();
        if (!parent?.id) return;
        const { data: card } = await supabase
          .from('card_payment_methods')
          .select('stripe_payment_method_id, brand, last4, is_active')
          .eq('parent_id', parent.id)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .maybeSingle();
        if (card && (card as { is_active?: boolean }).is_active !== false) {
          const c = card as { stripe_payment_method_id: string; brand: string | null; last4: string | null };
          setSavedCard({
            stripe_payment_method_id: c.stripe_payment_method_id,
            brand: c.brand ?? null,
            last4: c.last4 ?? null,
          });
          setUseSavedCard(true);
        }
      } catch (_) {
        // ignore silently in UI; tests may not mock supabase fully
      }
    })();
  }, [user?.email]);

  // The processing fee depends on which card is charged (a saved `link` card is a bank
  // payment at a lower rate), so tell the checkout whenever that changes.
  const chargedCardBrand = savedCard && useSavedCard ? savedCard.brand : null;
  useEffect(() => {
    onChargedCardBrandChange?.(chargedCardBrand);
  }, [chargedCardBrand, onChargedCardBrandChange]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (processingTimeout) {
        clearTimeout(processingTimeout);
      }
    };
  }, [processingTimeout]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    setError(null);
    setIsProcessing(true);
    let paymentSuccessful = false;
    // Kept outside the try so the catch below can report why the server refused.
    let serverError: { error?: string; details?: string; message?: string } | null = null;

    const timeout = setTimeout(() => {
      setIsProcessing(false);
      setError('Payment request timed out. Please try again.');
    }, 30000);
    setProcessingTimeout(timeout);

    if (!stripe || !elements) {
      setError('Stripe has not been initialized.');
      setIsProcessing(false);
      return;
    }

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (!token) {
        throw new Error('You must be logged in to make a payment.');
      }

      // Create payment intent
      const response = await fetch(
        ENDPOINTS.createPaymentIntent,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ 
            amount,
            payment_method_type: selectedPaymentMethod,
            setup_intent_id: selectedPaymentMethod === 'bank_account' ? await getBankAccountSetupIntentId() : undefined
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as {
          error?: string;
          details?: string;
          message?: string;
        };
        serverError = errorData;
        if (response.status === 401) {
          throw new Error('Authentication failed. Please try logging in again.');
        }
        // create-payment-intent returns its reason under `error`, with the Stripe
        // message under `details` - reading `message` swallowed every one of them.
        const serverMessage = errorData.error === 'Stripe API error'
          ? errorData.details
          : errorData.error || errorData.details || errorData.message;
        throw new Error(serverMessage || 'Failed to create payment intent');
      }

      const { clientSecret } = await response.json();

      if (!clientSecret) {
        throw new Error('No client secret received from the server');
      }

      let paymentResult;

      if (selectedPaymentMethod === 'card') {
        if (savedCard && useSavedCard) {
          paymentResult = await stripe.confirmCardPayment(clientSecret, {
            payment_method: savedCard.stripe_payment_method_id,
          });
        } else {
          paymentResult = await stripe.confirmCardPayment(
            clientSecret,
            {
              payment_method: {
                card: elements.getElement(CardElement)!,
              },
            }
          );
        }
      } else if (selectedPaymentMethod === 'zelle') {
        // For Zelle, we'll create a pending payment and provide Zelle instructions
        const result = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/payment-confirmation`,
            payment_method_data: {
              billing_details: {
                name: 'Customer Name', // You might want to collect this from the user
              },
            },
          },
        });
        paymentResult = result;
      } else if (selectedPaymentMethod === 'bank_account') {
        // For bank account payments, confirm the payment intent with the existing payment method
        paymentResult = await stripe.confirmPayment({
          clientSecret,
          confirmParams: {
            return_url: `${window.location.origin}/payment-confirmation?invoice_id=${invoiceId}`,
          },
          redirect: 'if_required',
        });
      }

      if (processingTimeout) {
        clearTimeout(processingTimeout);
      }

      if (paymentResult?.error) {
        console.error('Payment failed:', paymentResult.error);
        let errorMessage = 'Payment failed. ';
        
        if (paymentResult.error.type === 'card_error') {
          switch (paymentResult.error.code) {
            case 'card_declined':
              errorMessage += 'Your card was declined. Please try a different card.';
              break;
            case 'expired_card':
              errorMessage += 'Your card has expired. Please try a different card.';
              break;
            case 'incorrect_cvc':
              errorMessage += 'The CVC number was incorrect.';
              break;
            case 'processing_error':
              errorMessage += 'An error occurred while processing your card. Please try again.';
              break;
            default:
              errorMessage += paymentResult.error.message || 'Please check your card details and try again.';
          }
        } else {
          errorMessage += paymentResult.error.message || 'An unexpected error occurred.';
        }
        
        // Track payment failure
        if (invoiceId) {
          analytics.trackPaymentFailed({
            user,
            invoiceId,
            amount,
            paymentMethod: selectedPaymentMethod,
            errorMessage,
            sessionId: getSessionId(),
            metadata: {
              stripe_error_type: paymentResult.error.type,
              stripe_error_code: paymentResult.error.code,
              stripe_error_message: paymentResult.error.message
            }
          });
        }
        
        setError(errorMessage);
        setIsProcessing(false);
        onError(errorMessage);
        return;
      }

      if (paymentResult?.paymentIntent?.status === 'succeeded') {
        paymentSuccessful = true;
        onSuccess(paymentResult.paymentIntent.id, paymentResult.paymentIntent);
        // Don't reset form here - let the parent component handle navigation
        // resetForm();
      } else if (paymentResult?.paymentIntent?.status === 'processing') {
        // ACH payments go into "processing" status - this is normal
        if (selectedPaymentMethod === 'bank_account') {
          // Show success message for ACH payments in processing
          paymentSuccessful = true;
          onSuccess(paymentResult.paymentIntent.id, paymentResult.paymentIntent);
          // Don't reset form here - let the parent component handle navigation
          // resetForm();
        } else {
          setError('Payment is being processed. Please wait...');
        }
      } else if (paymentResult?.paymentIntent?.status === 'requires_payment_method') {
        setError('Your payment method was declined. Please try a different payment method.');
      } else if (paymentResult?.paymentIntent?.status === 'requires_action') {
        setError('Additional verification required. Please complete the verification process.');
      } else {
        setError(`Payment status: ${paymentResult?.paymentIntent?.status}. Please contact support if this persists.`);
      }
    } catch (error) {
      console.error('Payment error:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      
      // Track payment failure
      if (invoiceId) {
        analytics.trackPaymentFailed({
          user,
          invoiceId,
          amount,
          paymentMethod: selectedPaymentMethod,
          errorMessage,
          sessionId: getSessionId(),
          metadata: {
            error_source: 'payment_processing',
            error_type: error instanceof Error ? error.constructor.name : 'unknown',
            server_error: serverError ?? undefined
          }
        });
      }
      
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      // Only reset processing state if payment was not successful
      // Don't reset on success to prevent flash during navigation
      if (!paymentSuccessful) {
        setIsProcessing(false);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="payment-form">
      <div className="space-y-4">
        {/* We've moved payment method selection to the parent component, so we no longer need these buttons here */}
        
        {selectedPaymentMethod === 'card' && (
          <div className="bg-gray-50 p-4 rounded-lg" data-testid="card-payment-section">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Card Details
            </label>
            {savedCard && useSavedCard ? (
              <div className="flex items-center justify-between gap-3 p-3 border border-gray-200 rounded-xl bg-white">
                <div className="flex items-center gap-3 min-w-0">
                  <PaymentMethodIcon type="card" brand={savedCard.brand} size={24} />
                  <span className="flex items-baseline gap-1 text-sm font-medium text-gray-900 min-w-0">
                    {isLinkBankBrand(savedCard.brand) ? (
                      <span className="truncate min-w-0">{LINK_BANK_CARD_NAME}</span>
                    ) : (
                      <>
                        <span className="truncate min-w-0">{savedCard.brand ? savedCard.brand.charAt(0).toUpperCase() + savedCard.brand.slice(1) : 'Card'}</span>
                        <span className="shrink-0 whitespace-nowrap">•••• {savedCard.last4 ?? '••••'}</span>
                      </>
                    )}
                  </span>
                </div>
                <div className="relative shrink-0">
                  <button
                    type="button"
                    onClick={() => setCardMenuOpen((o) => !o)}
                    aria-label="Card options"
                    aria-haspopup="menu"
                    aria-expanded={cardMenuOpen}
                    className={`p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors ${
                      cardMenuOpen ? 'bg-gray-100 text-gray-600' : ''
                    }`}
                  >
                    <MoreHorizontal className="w-5 h-5" />
                  </button>
                  {cardMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setCardMenuOpen(false)} aria-hidden="true" />
                      <div role="menu" className="absolute right-0 top-9 z-20 w-48 rounded-xl border border-gray-200 bg-white shadow-lg py-1 overflow-hidden">
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setCardMenuOpen(false); setUseSavedCard(false); }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-gray-700 hover:bg-gray-50"
                        >
                          <CreditCard className="w-4 h-4" />
                          Use a different card
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div>
                {savedCard && (
                  <button
                    type="button"
                    className="mb-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                    onClick={() => setUseSavedCard(true)}
                  >
                    Use saved card ({isLinkBankBrand(savedCard.brand)
                      ? LINK_BANK_CARD_NAME
                      : `${savedCard.brand ? savedCard.brand.charAt(0).toUpperCase() + savedCard.brand.slice(1) : 'Card'} •••• ${savedCard.last4 ?? '••••'}`})
                  </button>
                )}
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#424770',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                      },
                      invalid: {
                        color: '#9e2146',
                      },
                    },
                  }}
                  className="p-3 border border-gray-300 rounded-md"
                />
              </div>
            )}
          </div>
        )}

        {selectedPaymentMethod === 'bank_account' && (
          <div className="bg-gray-50 p-4 rounded-xl" data-testid="bank-account-payment-section">
            <p className="text-sm text-gray-600">
              ACH bank transfers typically take 3-5 business days to process. This payment will be automatically debited from your connected bank account.
            </p>
          </div>
        )}

        {selectedPaymentMethod === 'zelle' && (
          <div className="bg-gray-50 p-4 rounded-lg" data-testid="zelle-payment-section">
            <div className="flex items-center flex-wrap gap-2 mb-4">
              <span className="text-gray-700 font-medium">Recipient Email:</span>
              <button
                type="button"
                onClick={() => copyToClipboard('billing@bhcskids.com', 'email')}
                className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 text-yellow-900 font-extrabold px-4 py-1 rounded-full text-lg shadow-sm hover:bg-yellow-100 active:bg-yellow-200 transition-colors"
                style={{ letterSpacing: '0.05em' }}
                title="Click to copy"
              >
                {copiedField === 'email'
                  ? <Check size={14} className="text-green-600 flex-shrink-0" />
                  : <Copy size={14} className="flex-shrink-0 opacity-50" />}
                billing@bhcskids.com
              </button>
              <span className="text-xs text-gray-500">
                {copiedField === 'email' ? '✓ Copied!' : 'click&copy'}
              </span>
            </div>
            <div className="flex items-center flex-wrap gap-2 mb-4">
              <span className="text-gray-700 font-medium">Memo Code:</span>
              <button
                type="button"
                onClick={() => memoCode && copyToClipboard(memoCode, 'memo')}
                disabled={!memoCode}
                className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 text-yellow-900 font-extrabold px-4 py-1 rounded-full text-lg shadow-sm hover:bg-yellow-100 active:bg-yellow-200 transition-colors disabled:cursor-default disabled:opacity-70"
                style={{ letterSpacing: '0.05em' }}
                title="Click to copy"
              >
                {copiedField === 'memo'
                  ? <Check size={14} className="text-green-600 flex-shrink-0" />
                  : <Copy size={14} className="flex-shrink-0 opacity-50" />}
                {memoCode || 'N/A'}
              </button>
              <span className="text-xs text-gray-500">
                {copiedField === 'memo' ? '✓ Copied!' : 'click&copy'}
              </span>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                <strong>Don't forget to enter the memo code in the 'Message/Memo to Recipient' field when making your Zelle payment</strong>
              </p>
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between text-xs text-gray-600">
                  <span className="text-gray-400">Need help?</span>
                                      <div className="flex items-center space-x-2">
                      <a href="/faq#faq-10" className="flex items-center px-3 py-1 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors border border-blue-200 text-xs font-medium">
                        <span className="mr-1">📋</span>
                        <span>How to pay</span>
                      </a>
                      <a href="/faq#faq-6" className="flex items-center px-3 py-1 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors border border-blue-200 text-xs font-medium">
                        <span className="mr-1">💰</span>
                        <span>Payment limits</span>
                      </a>
                    </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {selectedPaymentMethod !== 'zelle' && (
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className={`w-full py-3 px-4 rounded-md text-white font-medium
            ${isProcessing ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}
            transition-colors duration-200`}
          data-testid="pay-button"
        >
          {isProcessing ? 'Processing...' : `${t('action.pay')} $${amount.toFixed(2)}`}
        </button>
      )}

      {error && (
        <div className="text-red-500 mt-2 text-center">
          {error}
        </div>
      )}
    </form>
  );
} 